import React from 'react';
import { Brain, Settings, Database, MessageSquare, Target, RefreshCw } from 'lucide-react';

const AIManagement = () => {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-foreground">Gestión del Mentor IA</h1>
        <button className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent/90">
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualizar Modelo
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="dark-card p-6">
          <div className="flex items-center mb-4">
            <Brain className="h-6 w-6 text-accent" />
            <h2 className="ml-3 text-lg font-medium text-foreground">Rendimiento del Modelo</h2>
          </div>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-foreground/70">Precisión</span>
                <span className="text-sm font-medium text-foreground">95%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full">
                <div className="h-2 bg-green-500 rounded-full" style={{ width: '95%' }}></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-foreground/70">Tiempo de Respuesta</span>
                <span className="text-sm font-medium text-foreground">0.8s</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full">
                <div className="h-2 bg-blue-500 rounded-full" style={{ width: '85%' }}></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-foreground/70">Satisfacción Usuario</span>
                <span className="text-sm font-medium text-foreground">4.8/5</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full">
                <div className="h-2 bg-purple-500 rounded-full" style={{ width: '92%' }}></div>
              </div>
            </div>
          </div>
        </div>

        <div className="dark-card p-6">
          <div className="flex items-center mb-4">
            <Settings className="h-6 w-6 text-accent" />
            <h2 className="ml-3 text-lg font-medium text-foreground">Configuración</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground/70 mb-1">
                Temperatura del Modelo
              </label>
              <input
                type="range"
                min="0"
                max="100"
                defaultValue="70"
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-foreground/50 mt-1">
                <span>Conservador</span>
                <span>Creativo</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground/70 mb-1">
                Longitud Máxima de Respuesta
              </label>
              <select className="w-full border border-border rounded-lg bg-secondary/30 px-3 py-2">
                <option>Corta (256 tokens)</option>
                <option>Media (512 tokens)</option>
                <option>Larga (1024 tokens)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground/70 mb-1">
                Modo de Feedback
              </label>
              <select className="w-full border border-border rounded-lg bg-secondary/30 px-3 py-2">
                <option>Detallado</option>
                <option>Conciso</option>
                <option>Balanceado</option>
              </select>
            </div>
          </div>
        </div>

        <div className="dark-card p-6">
          <div className="flex items-center mb-4">
            <Database className="h-6 w-6 text-accent" />
            <h2 className="ml-3 text-lg font-medium text-foreground">Base de Conocimiento</h2>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-foreground/70">Documentos</span>
              <span className="text-sm font-medium text-foreground">1,234</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-foreground/70">Última Actualización</span>
              <span className="text-sm font-medium text-foreground">Hace 2 horas</span>
            </div>
            <button className="w-full inline-flex items-center justify-center px-4 py-2 border border-border rounded-md text-sm font-medium text-foreground hover:bg-secondary/30">
              Actualizar Base de Conocimiento
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="dark-card p-6">
          <div className="flex items-center mb-4">
            <MessageSquare className="h-6 w-6 text-accent" />
            <h2 className="ml-3 text-lg font-medium text-foreground">Interacciones Recientes</h2>
          </div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-start space-x-3 p-3 rounded-lg bg-secondary/30">
                <div className="h-8 w-8 rounded-full bg-accent/10 flex items-center justify-center text-accent">
                  U{i}
                </div>
                <div className="flex-1">
                  <p className="text-sm text-foreground">
                    "¿Cómo puedo mejorar mi roadmap de producto?"
                  </p>
                  <p className="text-xs text-foreground/70 mt-1">
                    Hace {i * 5} minutos
                  </p>
                </div>
                <div className="text-xs text-green-600">98% precisión</div>
              </div>
            ))}
          </div>
        </div>

        <div className="dark-card p-6">
          <div className="flex items-center mb-4">
            <Target className="h-6 w-6 text-accent" />
            <h2 className="ml-3 text-lg font-medium text-foreground">Métricas de Entrenamiento</h2>
          </div>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-foreground/70">Progreso de Entrenamiento</span>
                <span className="text-sm font-medium text-foreground">89%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full">
                <div className="h-2 bg-accent rounded-full" style={{ width: '89%' }}></div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 rounded-lg bg-secondary/30">
                <div className="text-sm text-foreground/70">Épocas Completadas</div>
                <div className="text-lg font-medium text-foreground">245/300</div>
              </div>
              <div className="p-3 rounded-lg bg-secondary/30">
                <div className="text-sm text-foreground/70">Loss Actual</div>
                <div className="text-lg font-medium text-foreground">0.0023</div>
              </div>
              <div className="p-3 rounded-lg bg-secondary/30">
                <div className="text-sm text-foreground/70">Ejemplos Procesados</div>
                <div className="text-lg font-medium text-foreground">1.2M</div>
              </div>
              <div className="p-3 rounded-lg bg-secondary/30">
                <div className="text-sm text-foreground/70">Tiempo Restante</div>
                <div className="text-lg font-medium text-foreground">2h 30m</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIManagement;